import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '@/config/firebase';
import { ROUTES } from '@/app/router/routes';
import { analyticsService } from '@/services/analytics/analyticsService';
import { redirectToSignIn, SIGN_IN_TO_APPLY } from '../utils/signInRequired';

/** A second click on the same job's Apply within this window doesn't open another tab. */
const REPEAT_WINDOW_MS = 1500;

/**
 * Opens a job's application form (or Easy Apply) — Flutter navigateTo(applicationRoute,
 * launchOtherTab: true): a new tab, after the screen_view Flutter logs with the call site's
 * analyticsClass. Callers store the application origin first, as before.
 *
 * Signed out, the visitor is told sign-in is needed and goes to /login in this tab, which returns
 * to the application afterwards (state.from), instead of a new tab per click that lands on the
 * login page (signInRequired.ts).
 */
export function useOpenApplication() {
  const navigate = useNavigate();
  const lastOpened = useRef<{ path: string; at: number } | null>(null);

  return useCallback(
    (displayId: string | undefined, isEasyApply: boolean, screenClass?: string) => {
      const route = isEasyApply ? ROUTES.EASY_APPLY : ROUTES.APPLICATION;
      const path = `${route}/${displayId ?? ''}`;

      if (!auth.currentUser) {
        redirectToSignIn(navigate, SIGN_IN_TO_APPLY, { from: { pathname: path } });
        return;
      }

      const now = Date.now();
      if (lastOpened.current?.path === path && now - lastOpened.current.at < REPEAT_WINDOW_MS) return;
      lastOpened.current = { path, at: now };
      void analyticsService.logScreenView({ screenName: route, screenClass });
      window.open(path, '_blank');
    },
    [navigate],
  );
}
